import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { CONTENT_STYLING } from 'constants';
import {
  BookAppearanceModifiersContext,
  BookAppearanceValuesContext,
  BookValuesContext,
  BookLocationValuesContext,
  DocumentValuesContext,
} from 'contexts';
import { getLocalSettingsProxy } from 'utilities';
import { useDimensions } from 'hooks';

export const BookAppearanceProvider = ({ children }) => {
  const { book } = useContext(BookValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { frameContainer } = useContext(DocumentValuesContext);

  const [lastFontSizeWasFit, setLastFontSizeWasFit] = useState(false);
  const [previousFontSize, setPreviousFontSize] = useState(null);

  const { dimensions } = useDimensions(frameContainer);

  const [localSettings] = useState(() => {
    return getLocalSettingsProxy(book.pk);
  });

  const getZoomToFitPercentage = useCallback(() => {
    if (
      !frameContainer?.clientWidth ||
      !currentBookLocation.document.fixedWidth
    ) {
      return;
    }

    return (
      frameContainer?.clientWidth / currentBookLocation.document.fixedWidth
    );
  }, [currentBookLocation.document.fixedWidth, frameContainer]);

  const isAllowedValue = useCallback((array, value) => {
    const valueArray = array.map((a) => {
      return a.value;
    });

    return valueArray.includes(value);
  }, []);

  // font family
  const [fontFamily, _setFontFamily] = useState(
    localSettings.contentStyling.fontFamily,
  );
  const setFontFamily = useCallback(
    (newValue) => {
      if (isAllowedValue(CONTENT_STYLING.OPTIONS.FONT_FAMILY, newValue)) {
        localSettings.contentStyling.fontFamily = newValue;
        _setFontFamily(newValue);
      }
    },
    [_setFontFamily, isAllowedValue, localSettings],
  );

  // font size
  const [fontSize, _setFontSize] = useState(
    localSettings.contentStyling.fontSize,
  );
  const setFontSize = useCallback(
    (newValue) => {
      localSettings.contentStyling.fontSize = parseFloat(newValue);
      _setFontSize(newValue);
    },
    [_setFontSize, localSettings],
  );

  const [isFitToScreen, setIsFitToScreen] = useState(null);
  useEffect(() => {
    setIsFitToScreen(fontSize === getZoomToFitPercentage());
  }, [fontSize, getZoomToFitPercentage, dimensions]);

  // letter spacing
  const [letterSpacing, _setLetterSpacing] = useState(
    localSettings.contentStyling.letterSpacing,
  );
  const setLetterSpacing = useCallback(
    (newValue) => {
      if (isAllowedValue(CONTENT_STYLING.OPTIONS.LETTER_SPACING, newValue)) {
        localSettings.contentStyling.letterSpacing = newValue;
        _setLetterSpacing(newValue);
      }
    },
    [_setLetterSpacing, isAllowedValue, localSettings],
  );

  // line height
  const [lineHeight, _setLineHeight] = useState(
    localSettings.contentStyling.lineHeight,
  );
  const setLineHeight = useCallback(
    (newValue) => {
      newValue = parseFloat(newValue);
      if (isAllowedValue(CONTENT_STYLING.OPTIONS.LINE_HEIGHT, newValue)) {
        localSettings.contentStyling.lineHeight = newValue;
        _setLineHeight(newValue);
      }
    },
    [_setLineHeight, isAllowedValue, localSettings],
  );

  // page margin
  const [pageMargin, _setPageMargin] = useState(
    localSettings.contentStyling.pageMargin,
  );
  const setPageMargin = useCallback(
    (newValue) => {
      newValue = parseFloat(newValue);
      if (isAllowedValue(CONTENT_STYLING.OPTIONS.PAGE_MARGIN, newValue)) {
        localSettings.contentStyling.pageMargin = newValue;
        _setPageMargin(newValue);
      }
    },
    [_setPageMargin, isAllowedValue, localSettings],
  );

  // theme
  const [theme, _setTheme] = useState(localSettings.contentStyling.theme);
  const setTheme = useCallback(
    (newValue) => {
      if (isAllowedValue(CONTENT_STYLING.OPTIONS.THEME, newValue)) {
        localSettings.contentStyling.theme = newValue;
        _setTheme(newValue);
      }
    },
    [_setTheme, isAllowedValue, localSettings],
  );

  // Set all style properties to Readium defaults.
  const resetBookStyling = useCallback(() => {
    localSettings.contentStyling.fontFamily =
      CONTENT_STYLING.DEFAULTS.FONT_FAMILY;
    _setFontFamily(CONTENT_STYLING.DEFAULTS.FONT_FAMILY);

    localSettings.contentStyling.fontSize = CONTENT_STYLING.DEFAULTS.FONT_SIZE;
    _setFontSize(CONTENT_STYLING.DEFAULTS.FONT_SIZE);

    localSettings.contentStyling.letterSpacing =
      CONTENT_STYLING.DEFAULTS.LETTER_SPACING;
    _setLetterSpacing(CONTENT_STYLING.DEFAULTS.LETTER_SPACING);

    localSettings.contentStyling.lineHeight =
      CONTENT_STYLING.DEFAULTS.LINE_HEIGHT;
    _setLineHeight(CONTENT_STYLING.DEFAULTS.LINE_HEIGHT);

    localSettings.contentStyling.pageMargin =
      CONTENT_STYLING.DEFAULTS.PAGE_MARGIN;
    _setPageMargin(CONTENT_STYLING.DEFAULTS.PAGE_MARGIN);

    localSettings.contentStyling.theme = CONTENT_STYLING.DEFAULTS.THEME;
    _setTheme(CONTENT_STYLING.DEFAULTS.THEME);

    setPreviousFontSize(null);
    setLastFontSizeWasFit(false);
  }, [
    localSettings,
    _setFontFamily,
    _setFontSize,
    _setLetterSpacing,
    _setLineHeight,
    _setPageMargin,
    _setTheme,
  ]);

  const handleZoomFit = useCallback(
    (shouldSetPreviousFontSize = true) => {
      setLastFontSizeWasFit(true);
      setPreviousFontSize(fontSize);

      if (lastFontSizeWasFit && previousFontSize && shouldSetPreviousFontSize) {
        setFontSize(previousFontSize);
      } else {
        setFontSize(getZoomToFitPercentage());
      }
    },
    [
      getZoomToFitPercentage,
      fontSize,
      lastFontSizeWasFit,
      previousFontSize,
      setFontSize,
    ],
  );

  const [themeBackgroundColor, setThemeBackgroundColor] = useState();

  useEffect(() => {
    switch (localSettings.contentStyling.theme) {
      case CONTENT_STYLING.OPTIONS.THEME_HIGH_CONTRAST:
        return setThemeBackgroundColor({ r: 255, g: 255, b: 255 });
      case CONTENT_STYLING.OPTIONS.THEME_SEPIA:
        return setThemeBackgroundColor({ r: 250, g: 244, b: 232 });
      case CONTENT_STYLING.OPTIONS.THEME_NIGHT:
        return setThemeBackgroundColor({ r: 0, g: 0, b: 0 });
      default:
        return setThemeBackgroundColor({ r: 255, g: 255, b: 255 });
    }
  }, [localSettings, theme]);

  useEffect(
    function setInitialFontSize() {
      // If the zoom level is already set to something other than the default, do nothing.
      // Past this point, we handle cases where the user is viewing a specific title on a specific device for the first time.
      if (
        fontSize !== CONTENT_STYLING.DEFAULTS.FONT_SIZE ||
        !frameContainer?.clientWidth
      ) {
        return;
      }

      // If the title has a fixed width, but there's not enough horizontal space to display it,
      // compute the value that represents zooming the content to fit the frame. Save that value as the default.
      if (
        currentBookLocation.document.fixedWidth &&
        currentBookLocation.document.fixedWidth > frameContainer?.clientWidth
      ) {
        const fit = getZoomToFitPercentage();
        return setFontSize(fit);
      }

      // If the content is not fixed width, or the fixed width content is smaller than the frame, default to 1.
      // For fluid width titles, this represents a font size of 100%.
      // For fixed width titles, this represents a resize value of 100%.
      setFontSize(1);
    },
    [
      currentBookLocation.document.fixedWidth,
      fontSize,
      frameContainer,
      getZoomToFitPercentage,
      setFontSize,
    ],
  );

  const values = useMemo(
    () => ({
      fontFamily,
      fontSize,
      letterSpacing,
      lineHeight,
      pageMargin,
      theme,
      lastFontSizeWasFit,
      previousFontSize,
      themeBackgroundColor,
      isFitToScreen,
    }),
    [
      fontFamily,
      fontSize,
      letterSpacing,
      lineHeight,
      pageMargin,
      theme,
      lastFontSizeWasFit,
      previousFontSize,
      themeBackgroundColor,
      isFitToScreen,
    ],
  );

  const modifiers = useMemo(
    () => ({
      resetBookStyling,
      setFontFamily,
      setFontSize,
      setLetterSpacing,
      setLineHeight,
      setPageMargin,
      setTheme,
      setLastFontSizeWasFit,
      setPreviousFontSize,
      handleZoomFit,
    }),
    [
      resetBookStyling,
      setFontFamily,
      setFontSize,
      setLetterSpacing,
      setLineHeight,
      setPageMargin,
      setTheme,
      setLastFontSizeWasFit,
      setPreviousFontSize,
      handleZoomFit,
    ],
  );

  return (
    <BookAppearanceValuesContext.Provider value={values}>
      <BookAppearanceModifiersContext.Provider value={modifiers}>
        {children}
      </BookAppearanceModifiersContext.Provider>
    </BookAppearanceValuesContext.Provider>
  );
};

export default BookAppearanceProvider;
